import { MapArtifactToSlides } from 'interfaces/jobs/basic';

export interface CellSubsamplingParams {
  capPerSlide: number;
  totalN: number;
  threshold: number;
  minGroupSize: number;
  topK: number;
}

export interface LouvainParams {
  nDims: number | null;
  resolution: number;
}

export interface MifMarkerUnsupervisedAnalysisJobConfig {
  binaryClassifierArtifactUrls: MapArtifactToSlides[];
  markers: string[];
  manifest: string[];
  cellSubsamplingParams: CellSubsamplingParams;
  louvainParams: LouvainParams;
}

export const DEFAULT_CELL_SUBSAMPLING_PARAMS: CellSubsamplingParams = {
  capPerSlide: 100000,
  totalN: 1000,
  threshold: 0.5,
  minGroupSize: 10,
  topK: 75,
};

export const DEFAULT_LOUVAIN_PARAMS: LouvainParams = {
  nDims: null,
  resolution: 0.3,
};

export const createDefaultMifMarkerUnsupervisedAnalysisJobConfig = (): MifMarkerUnsupervisedAnalysisJobConfig => ({
  binaryClassifierArtifactUrls: [],
  markers: [],
  manifest: [],
  cellSubsamplingParams: { ...DEFAULT_CELL_SUBSAMPLING_PARAMS },
  louvainParams: { ...DEFAULT_LOUVAIN_PARAMS },
});

export const applyDefaultMifMarkerUnsupervisedAnalysisJobConfig = (
  config?: Partial<MifMarkerUnsupervisedAnalysisJobConfig> | null
): MifMarkerUnsupervisedAnalysisJobConfig => {
  const defaultConfig = createDefaultMifMarkerUnsupervisedAnalysisJobConfig();

  return {
    ...defaultConfig,
    ...config,
    binaryClassifierArtifactUrls: config?.binaryClassifierArtifactUrls ?? defaultConfig.binaryClassifierArtifactUrls,
    markers: config?.markers ?? defaultConfig.markers,
    manifest: config?.manifest ?? defaultConfig.manifest,
    cellSubsamplingParams: {
      ...defaultConfig.cellSubsamplingParams,
      ...(config?.cellSubsamplingParams ?? {}),
    },
    louvainParams: {
      ...defaultConfig.louvainParams,
      ...(config?.louvainParams ?? {}),
    },
  };
};

export interface MifMarkerUnsupervisedAnalysisFormValues {
  jobName: string;
  jobDescription: string;
  configParams: MifMarkerUnsupervisedAnalysisJobConfig;
}

export const createDefaultMifMarkerUnsupervisedAnalysisFormValues = (): MifMarkerUnsupervisedAnalysisFormValues => ({
  jobName: '',
  jobDescription: '',
  configParams: createDefaultMifMarkerUnsupervisedAnalysisJobConfig(),
});

export enum RunMifMarkerUnsupervisedAnalysisStep {
  LoadParams = 'Reuse Previous Job Settings',
  NameAndDescription = 'Name and Description',
  SelectBinaryClassifierResults = 'Select Binary Classifier Results',
  AdditionalParameters = 'Additional Parameters',
}

export const runMifMarkerUnsupervisedAnalysisOrder = [
  RunMifMarkerUnsupervisedAnalysisStep.LoadParams,
  RunMifMarkerUnsupervisedAnalysisStep.NameAndDescription,
  RunMifMarkerUnsupervisedAnalysisStep.SelectBinaryClassifierResults,
  RunMifMarkerUnsupervisedAnalysisStep.AdditionalParameters,
];
